import React, { useMemo, useRef } from 'react';

import Box from '@material-ui/core/Box';
import SvgIcon from '@material-ui/core/SvgIcon';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import classnames from 'classnames';
import { TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL } from 'config/urls';
import { isEmpty, map } from 'lodash';

import StudentWorkCard from 'pages/Teachers/Insights/components/GradeOverview/components/StudentWorkCard';
import StudentWorkCardSkeleton from 'pages/Teachers/Insights/components/GradeOverview/components/StudentWorkCardSkeleton';
import { useTouchSideScroll } from 'utils/touchScroll';

import Button from 'components/Button';
import NoResultsPlaceholder from 'components/NoResultsPlaceholder';
import Typography from 'components/Typography';

import styles from './styles.module.scss';

const StudentWorkList = ({
  studentWorkList,
  filters,
  buildAssignmentFeedbackLinkObject,
  showScores,
  multiSelectMode,
  addComment,
  pulseEffectTriggered,
  onPulseEffectEnd,
  toggleSelectStudentWorkItem,
  width,
  activeTasksIds,
  storeActiveTaskId,
  removeActiveTaskId,
  setSelectedWorksSelectedChildren,
  refetchStudentWork,
  handleStudentWorkClick
}) => {
  const touchScrollContainerRef = useRef();
  const stopTouchScroll = useTouchSideScroll(touchScrollContainerRef, []);

  const onAnimationEnd = () => {
    if (pulseEffectTriggered) {
      onPulseEffectEnd();
    }
  };

  const visibleTaskIds = useMemo(
    () => map(filters.tasks, 'id'),
    [filters.tasks]
  );
  const showAllStudentsAllTasks = useMemo(
    () => filters.show.value === 'all_students_all_tasks',
    [filters.show]
  );
  const showAllSubmittedAllTasks = useMemo(
    () => filters.show.value === 'all_submitted_all_tasks',
    [filters.show]
  );

  const shouldSplitWorks = useMemo(() => {
    return showAllStudentsAllTasks || showAllSubmittedAllTasks;
  }, [showAllStudentsAllTasks, showAllSubmittedAllTasks]);

  const hasStudentWork = !isEmpty(studentWorkList);

  const lgScreen = isWidthUp('lg', width);

  const wrapperClassName = classnames({
    [styles.wrapper]: lgScreen,
    [styles.smallwrapper]: !lgScreen
  });

  return (
    <Box
      className={classnames(wrapperClassName, {
        [styles.allTasksWrapper]:
          showAllStudentsAllTasks || showAllSubmittedAllTasks
      })}
      onAnimationEnd={onAnimationEnd}
    >
      {!hasStudentWork && (
        <NoResultsPlaceholder
          text="to get started"
          button={
            <Button
              color="blue"
              className={styles.captureWorkButton}
              noTypography
              href={TEACHERS_BULK_UPLOAD_WORK_UPLOAD_AND_QR_URL}
            >
              <SvgIcon
                className={styles.cameraIcon}
                component={CameraAltIcon}
              />
              <Typography variant="H-TEXT-3">capture work</Typography>
            </Button>
          }
        />
      )}
      {hasStudentWork && (
        <div
          className={styles.studentCardsContainer}
          ref={touchScrollContainerRef}
        >
          {studentWorkList.map(
            ({ work, student, selected, waitingForFeedback }) => {
              if (waitingForFeedback && !student.is_teacher_demo_student) {
                return <StudentWorkCardSkeleton key={`${student.id}`} />;
              }

              if (!shouldSplitWorks) {
                return (
                  <StudentWorkCard
                    key={`${student.id}`}
                    student={student}
                    tasksData={work}
                    selected={selected}
                    showScores={showScores}
                    addComment={addComment}
                    multiSelectMode={multiSelectMode}
                    pulseEffectTriggered={pulseEffectTriggered}
                    toggleSelectStudentWorkItem={toggleSelectStudentWorkItem}
                    // this will be removed once the new student work dialog is deployed StudentWorkInsightsDialog
                    buildAssignmentFeedbackLinkObject={
                      buildAssignmentFeedbackLinkObject
                    }
                    activeTasksIds={activeTasksIds}
                    storeActiveTaskId={storeActiveTaskId}
                    removeActiveTaskId={removeActiveTaskId}
                    stopTouchScroll={stopTouchScroll}
                    setSelectedWorksSelectedChildren={
                      setSelectedWorksSelectedChildren
                    }
                    refetchStudentWork={refetchStudentWork}
                    handleStudentWorkClick={handleStudentWorkClick}
                  />
                );
              } else {
                return (
                  <>
                    {map(work, (workItem, index) => {
                      return (
                        <StudentWorkCard
                          key={`${student.id}_${index}`}
                          student={student}
                          tasksData={[workItem]}
                          selected={workItem.selected}
                          showScores={showScores}
                          addComment={addComment}
                          visibleTaskIds={visibleTaskIds}
                          multiSelectMode={multiSelectMode}
                          pulseEffectTriggered={pulseEffectTriggered}
                          toggleSelectStudentWorkItem={
                            toggleSelectStudentWorkItem
                          }
                          buildAssignmentFeedbackLinkObject={
                            buildAssignmentFeedbackLinkObject
                          }
                          activeTasksIds={activeTasksIds}
                          storeActiveTaskId={storeActiveTaskId}
                          removeActiveTaskId={removeActiveTaskId}
                          stopTouchScroll={stopTouchScroll}
                          setSelectedWorksSelectedChildren={
                            setSelectedWorksSelectedChildren
                          }
                          refetchStudentWork={refetchStudentWork}
                          handleStudentWorkClick={handleStudentWorkClick}
                        />
                      );
                    })}
                  </>
                );
              }
            }
          )}
        </div>
      )}
    </Box>
  );
};

export default withWidth()(StudentWorkList);
